import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { buildHotspotLoginUrl } from '../../utils/voucher_qr';
import { formatVoucherDuration } from './durationUtil';

const ElegantTemplate = ({ vouchers, companyLogo = '', companyName = '', hotspotGatewayIp, batchHeader}) => {
  return (
    <div className="print-container-elegant print-root">
      <style>{`
        @page {
          size: A4 landscape;
          margin: 2mm;
        }

        @media print {
          /* Reset all scaling on parents so 1mm = 1mm */
          html, body { width: 100%; height: 100%; margin: 0 !important; padding: 0 !important; background: white !important; }
          html *:not(.print-root):not(.print-root *) { visibility: hidden !important; }
          .print-root, .print-root * { visibility: visible !important; }
          .print-root {
            position: absolute !important;
            left: 0 !important; top: 0 !important;
            transform: none !important;
            width: 100% !important;
          }
        }

        
        .print-container-elegant {
          width: 297mm;
          margin: 0 auto;
          padding: 2mm;
          background: #f9fafb;
          display: flex;
          flex-wrap: wrap;
          gap: 0.5mm;
          align-content: flex-start;
        }
        
        @media print {
          .print-container-elegant {
            background: white;
            padding: 0;
          }
        }
        
        .elegant-card {
          width: calc((100% - 4 * 0.5mm) / 5);
          height: 40mm;
          border: 3px double #d4af37;
          border-radius: 2mm;
          padding: 0;
          box-sizing: border-box;
          background: linear-gradient(135deg, #ffffff 0%, #fffef8 100%);
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(212,175,55,0.15);
          page-break-inside: avoid;
          flex-shrink: 0;
        }
        
        .elegant-corner {
          position: absolute;
          width: 8mm;
          height: 8mm;
          border: 1.5px solid #d4af37;
        }
        
        .elegant-corner-tl {
          top: 0;
          left: 0;
          border-right: none;
          border-bottom: none;
        }
        
        .elegant-corner-tr {
          top: 0;
          right: 0;
          border-left: none;
          border-bottom: none;
        }
        
        .elegant-corner-bl {
          bottom: 0;
          left: 0;
          border-right: none;
          border-top: none;
        }
        
        .elegant-corner-br {
          bottom: 0;
          right: 0;
          border-left: none;
          border-top: none;
        }
        
        .elegant-content {
          position: relative;
          z-index: 1;
          height: 100%;
          display: flex;
          flex-direction: column;
          padding: 2mm;
        }
        
        .elegant-header {
          text-align: center;
          border-bottom: 2px solid #d4af37;
          padding-bottom: 1mm;
          margin-bottom: 1mm;
          position: relative;
        }
        
        .elegant-number {
          position: absolute;
          top: 0;
          right: 0;
          font-size: 5pt;
          font-weight: 700;
          color: #d4af37;
          background: #fffef8;
          padding: 0.5mm 1mm;
          border: 1px solid #d4af37;
          border-radius: 1mm;
        }
        
        .elegant-logo {
          margin-bottom: 0.8mm;
          margin-top: 0mm;
        }
        
        .elegant-logo img {
          width: 18mm;
          height: 6mm;
          object-fit: contain;
        }
        
        .elegant-price {
          font-size: 9pt;
          font-weight: 900;
          color: #d4af37;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        
        .elegant-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 1mm;
        }
        
        .elegant-code-section-wrapper {
          display: flex;
          gap: 1.5mm;
        }
        
        .elegant-left {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5mm;
        }
        
        .elegant-code-label {
          font-size: 4pt;
          font-weight: 700;
          color: #92784f;
          text-transform: uppercase;
          letter-spacing: 0.1mm;
        }
        
        .elegant-code-value {
          font-size: 7pt;
          font-weight: 800;
          color: #1f2937;
          letter-spacing: 0.3mm;
          padding: 0.8mm;
          border: 2px solid #d4af37;
          border-radius: 1mm;
          text-align: center;
          background: linear-gradient(135deg, #fffef8 0%, #ffffff 100%);
        }
        
        .elegant-info-section {
          margin-top: 0.5mm;
        }
        
        .elegant-info-item {
          font-size: 5pt;
          color: #374151;
          line-height: 1.4;
          margin-bottom: 0.3mm;
        }
        
        .elegant-info-item strong {
          font-weight: 800;
          color: #92784f;
        }
        
        .elegant-qr-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .elegant-qr {
          padding: 1mm;
          background: white;
          border: 2px solid #d4af37;
          border-radius: 1mm;
          box-shadow: 0 1px 3px rgba(212,175,55,0.2);
        }
        
        .elegant-footer {
          margin-top: auto;
          text-align: center;
          padding: 1mm;
          background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
          color: white;
          font-size: 4.5pt;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.15mm;
          border-radius: 0 0 1mm 1mm;
          margin: auto -2mm -2mm -2mm;
        }
      `}</style>
      
      {batchHeader}
      {vouchers.map((voucher, index) => (
        <div key={voucher.id || index} className="elegant-card" data-voucher-card>
          <div className="elegant-corner elegant-corner-tl"></div>
          <div className="elegant-corner elegant-corner-tr"></div>
          <div className="elegant-corner elegant-corner-bl"></div>
          <div className="elegant-corner elegant-corner-br"></div>
          
          <div className="elegant-content">
            <div className="elegant-header">
              <div className="elegant-number">#{index + 1}</div>
              <div className="elegant-logo">
                <img src={companyLogo || ""} alt="Logo" />
              </div>
              <div className="elegant-price">
                Rp.{(voucher.price || 0).toLocaleString('id-ID')}
              </div>
            </div>
            
            <div className="elegant-body">
              <div className="elegant-code-section-wrapper">
                <div className="elegant-left">
                  <div className="elegant-code-label">Kode Voucher</div>
                  <div className="elegant-code-value">{voucher.voucher_code}</div>
                </div>
                
                <div className="elegant-qr-wrapper">
                  <div className="elegant-qr">
                    <QRCodeSVG 
                      value={buildHotspotLoginUrl(voucher.username, voucher.password, hotspotGatewayIp)}
                      size={28}
                      level="M"
                    />
                  </div>
                </div>
              </div>
              
              <div className="elegant-info-section">
                <div className="elegant-info-item">
                  <strong>Masa Aktif:</strong> {formatVoucherDuration(voucher)}
                </div>
                <div className="elegant-info-item">
                  <strong>Username:</strong> {voucher.username}
                </div>
              </div>
            </div>
            
            <div className="elegant-footer">
              ★ Jangan Dibuang Selama Masih Aktif ★
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ElegantTemplate;
